/**
 * Typed API Client with interceptors and error handling
 * Provides a consistent interface for all API calls
 */
import { env } from '@shared/utils/env';

import { sanitizePathEdges } from '../string/path.util';

// Base types for API responses
export interface ApiResponse<T = unknown> {
  data: T;
  status: 'success' | 'error';
  timestamp: string;
}

export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, unknown>;
  status: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// Request configuration interface
export interface RequestConfig extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  /** When true, suppresses the api:gateway-unauthorized modal dispatch for this request. */
  suppressUnauthorized?: boolean;
  /** Internal flag — set by the client to skip the refresh-retry loop on the retry attempt. */
  _refreshAttempted?: boolean;
}

// HTTP methods
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// API Client class
export class ApiClient {
  private baseURL: string;
  private authTokenProvider?: () => Promise<string | null> | (() => string | null);
  private defaultTimeout: number = 10000;
  private defaultRetries: number = 0;
  private requestInterceptors: Array<
    (config: RequestConfig) => RequestConfig | Promise<RequestConfig>
  > = [];
  private responseInterceptors: Array<(response: Response) => Response | Promise<Response>> = [];
  private errorInterceptors: Array<(error: ApiError) => ApiError | Promise<ApiError>> = [];
  private userIdentity: string | null = null;
  private refreshHandler?: () => Promise<boolean>;
  private pendingRefresh?: Promise<boolean>;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || env.API_BASE_URL;
    this.setupDefaultInterceptors();
  }

  /**
   * Set a provider to retrieve the current access token (in-memory). The provider should
   * return a string token or null. It can be async.
   */
  public setAuthTokenProvider(provider: () => Promise<string | null> | (() => string | null)) {
    this.authTokenProvider = provider;
  }

  /**
   * Set the identity (email) of the authenticated user.
   * This value is sent as X-Support-Executed-By on every request for audit / Kibana traceability.
   */
  public setUserIdentity(identity: string | null): void {
    this.userIdentity = identity;
  }

  /**
   * Register a handler that attempts to refresh the session when a 401 is received.
   * The handler should return true on success (cookies will have been updated) or false on failure.
   * Pass undefined to remove the handler.
   */
  public setRefreshHandler(handler: (() => Promise<boolean>) | undefined): void {
    this.refreshHandler = handler;
  }

  /**
   * Add request interceptor
   */
  public addRequestInterceptor(
    interceptor: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>
  ): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  public addResponseInterceptor(
    interceptor: (response: Response) => Response | Promise<Response>
  ): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Add error interceptor
   */
  public addErrorInterceptor(interceptor: (error: ApiError) => ApiError | Promise<ApiError>): void {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Generic request method
   */
  public async request<T = unknown>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    // Attach auth header from provider if available
    let requestConfig = await this.attachAuthHeader({ ...config, credentials: 'include' });
    // Apply request interceptors
    // request interceptors may further modify headers or config
    for (const interceptor of this.requestInterceptors) {
      requestConfig = await interceptor(requestConfig); // modify config in chain
    }

    try {
      const response = await this.fetchWithTimeout(url, requestConfig);

      // 401 handling: attempt token refresh once, then retry original request.
      // A permission (authorization) denial is NOT a session problem, so it must skip the
      // refresh path — otherwise a spurious refresh can hang the request and the error never
      // surfaces to the caller.
      if (
        response.status === 401 &&
        this.refreshHandler &&
        !config._refreshAttempted &&
        !(await this.isAuthorizationDenial(response))
      ) {
        const refreshed = await this.doRefresh();
        if (refreshed) {
          return this.request<T>(endpoint, { ...config, _refreshAttempted: true });
        }
      }

      // Apply response interceptors
      let processedResponse = response;
      for (const interceptor of this.responseInterceptors) {
        processedResponse = await interceptor(processedResponse);
      }

      return await this.handleResponse<T>(processedResponse, config.suppressUnauthorized);
    } catch (error) {
      throw await this.handleError(error);
    }
  }

  /**
   * A 401 from the support-gateway can mean either "session expired" (refresh + retry) or
   * "you lack permission for this action" (@HasPermission denial). The latter is marked with
   * internal_term = UNAUTHORIZED_ACTION and must not trigger a token refresh. Reads a clone so
   * the original response body stays available for handleResponse.
   */
  private async isAuthorizationDenial(response: Response): Promise<boolean> {
    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return false;
    try {
      const body = (await response.clone().json()) as { internal_term?: string } | null;
      return body?.internal_term === 'UNAUTHORIZED_ACTION';
    } catch {
      return false;
    }
  }

  private async doRefresh(): Promise<boolean> {
    if (this.pendingRefresh) {
      return this.pendingRefresh;
    }
    this.pendingRefresh = (async () => {
      try {
        return await this.refreshHandler!();
      } catch {
        return false;
      } finally {
        this.pendingRefresh = undefined;
      }
    })();
    return this.pendingRefresh;
  }

  /**
   * GET request
   */
  public async get<T = unknown>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const url = params ? this.addQueryParams(endpoint, params) : endpoint;

    return this.request<T>(url, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  public async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  public async put<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  public async patch<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  public async delete<T = unknown>(
    endpoint: string,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * Upload file
   */
  public async upload<T = unknown>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const headers = { ...config?.headers } as Record<string, string>;
    delete headers['Content-Type']; // Remove Content-Type to let browser set it with boundary

    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: formData,
      headers,
    });
  }

  /**
   * Setup default interceptors
   */
  private setupDefaultInterceptors(): void {
    // Default request interceptor - add auth headers, content-type, etc.
    this.addRequestInterceptor((config) => {
      const headers = { Accept: 'application/json', ...(config.headers || {}) };

      // Don't force Content-Type if body is FormData/Blob/ArrayBufferView, or if already set
      const isJsonBody = typeof config.body === 'string' || config.body === undefined; // POST/PUT requests use JSON.stringify

      if (isJsonBody && !('Content-Type' in headers)) {
        (headers as Record<string, string>)['Content-Type'] = 'application/json';
      }

      return { ...config, headers } as RequestConfig;
    });

    // Default error interceptor - handle common error scenarios
    this.addErrorInterceptor(async (error) => {
      // Log errors in development
      if (env.IS_DEVELOPMENT) {
        console.error('API Error:', error);
      }

      return error;
    });

    // Add tracing id header for observability
    this.addRequestInterceptor((config) => {
      const headers = { ...(config.headers || {}) } as Record<string, string>;
      if (!headers['x-trace-id'] && !headers['X-Trace-Id']) {
        const id = `rid-${Math.random().toString(36).slice(2, 10)}`;
        headers['x-trace-id'] = id;
      }
      return { ...config, headers } as RequestConfig;
    });

    // Add user identity header for audit / Kibana traceability
    this.addRequestInterceptor((config) => {
      if (!this.userIdentity) return config;
      const headers = { ...(config.headers || {}) } as Record<string, string>;
      headers['X-Support-Executed-By'] = this.userIdentity;
      return { ...config, headers } as RequestConfig;
    });
  }

  /**
   * Helper: attempt to add Authorization header from token provider if available
   */
  private async attachAuthHeader(config: RequestConfig) {
    try {
      if (!this.authTokenProvider) return config;
      const tokenOrPromise = this.authTokenProvider();
      const token = tokenOrPromise instanceof Promise ? await tokenOrPromise : tokenOrPromise;
      if (token) {
        const headers = { ...(config.headers || {}) } as Record<string, string>;
        if (!headers['Authorization'] && !headers['authorization']) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        return { ...config, headers } as RequestConfig;
      }
      return config;
    } catch {
      return config;
    }
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }

    const baseUrl = sanitizePathEdges(this.baseURL, 'end');
    const path = sanitizePathEdges(endpoint, 'start');
    return `${baseUrl}/${path}`;
  }

  /**
   * Add query parameters to URL
   */
  private addQueryParams(
    endpoint: string,
    params: Record<string, string | number | boolean>
  ): string {
    // Handle relative URLs by using a symbolic base URL
    const url = new URL(
      endpoint.startsWith('http')
        ? endpoint
        : `https://localhost/${sanitizePathEdges(endpoint, 'start')}`
    );
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    // Return the relative path if we used a placeholder, otherwise return full URL
    return endpoint.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
  }

  /**
   * Fetch with timeout support
   */
  private async fetchWithTimeout(url: string, config: RequestConfig): Promise<Response> {
    const timeout = config.timeout ?? this.defaultTimeout;
    const retries = config.retries ?? this.defaultRetries;
    const baseDelay = config.retryDelay ?? 300;

    // By default, only retry idempotent methods
    const method = (config.method ?? 'GET').toUpperCase();
    const canRetryMethod = ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE'].includes(method);

    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeout);
      const signal = this.composeSignal(config.signal || undefined, controller.signal);

      try {
        const res = await fetch(url, { ...config, signal });

        clearTimeout(timeoutId);

        // 2xx => OK
        if (res.ok) return res;

        // 4xx => no reintentar salvo 429
        if (this.shouldRetry(res) === false) {
          return res;
        }

        // 5xx o 429 => evaluar retry
        if (!canRetryMethod || attempt === retries) return res;

        // Respeta Retry-After (segundos o fecha)
        const ra = res.headers.get('retry-after');
        const delayFromHeader = ra
          ? Number(ra)
            ? Number(ra) * 1000
            : Math.max(0, Date.parse(ra) - Date.now())
          : null;

        const delay = delayFromHeader ?? this.computeDelay(attempt, baseDelay);
        await new Promise((r) => setTimeout(r, delay));
      } catch (err: unknown) {
        clearTimeout(timeoutId);

        // Abortado por el caller => no reintentar
        if (signal?.aborted && !timedOut) {
          throw { message: 'Request aborted', code: 'ABORTED', status: 0 } as ApiError;
        }
        // Timeout => reintentar si aplica
        if (timedOut) {
          if (!canRetryMethod || attempt === retries) {
            throw { message: 'Request timeout', code: 'TIMEOUT', status: 0 } as ApiError;
          }
          await new Promise((r) => setTimeout(r, this.computeDelay(attempt, baseDelay)));
          continue;
        }
        // Errores de red transitorios => reintento
        if (!canRetryMethod || attempt === retries) {
          throw {
            message: (err as Error)?.message ?? 'Network error',
            code: 'NETWORK_ERROR',
            status: 0,
          } as ApiError;
        }
        await new Promise((r) => setTimeout(r, this.computeDelay(attempt, baseDelay)));
      }
    }
    throw { message: 'Max retries exceeded', code: 'RETRIES_EXCEEDED', status: 0 } as ApiError;
  }

  private composeSignal(...signals: (AbortSignal | undefined)[]) {
    const valid = signals.filter(Boolean) as AbortSignal[];
    if (valid.length === 0) return undefined;
    if (
      'any' in AbortSignal &&
      typeof (AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal })
        .any === 'function'
    ) {
      return (
        AbortSignal as typeof AbortSignal & { any: (signals: AbortSignal[]) => AbortSignal }
      ).any(valid);
    }
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    valid.forEach((s) => s.addEventListener('abort', onAbort, { once: true }));
    return controller.signal;
  }

  private shouldRetry(res: Response) {
    return res.status >= 400 && res.status < 500 && res.status !== 429;
  }

  private computeDelay(attempt: number, base = 300): number {
    const expo = base * Math.pow(2, attempt);
    const jitter = Math.random() * base;
    return expo + jitter;
  }

  /**
   * Handle successful response
   */
  private async handleResponse<T>(response: Response, suppressUnauthorized?: boolean): Promise<ApiResponse<T>> {
    const ct = response.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');

    if (!response.ok) {
      let errorData: Record<string, unknown> | undefined = undefined;
      if (isJson) {
        try {
          errorData = await response.json();
        } catch {
          /* ignore */
        }
      }

      // Capability denials (POLICY_DENIED) are also 403 but signal a missing permission,
      // not an invalid session — don't trigger the logout/redirect flow for them.
      const isCapabilityDenied = errorData?.error === 'POLICY_DENIED';
      if (!suppressUnauthorized && !isCapabilityDenied && (response.status === 403 || errorData?.error === 'support_gateway_unauthorized')) {
        window.dispatchEvent(new CustomEvent('api:gateway-unauthorized'));
      }

      throw {
        message: (errorData?.message as string) || (errorData?.description as string) || response.statusText || 'Request failed',
        code: ((errorData?.error as string) || (errorData?.code as string) || String(response.status)) as string,
        details: errorData?.details as Record<string, unknown> | undefined,
        status: response.status,
      } as ApiError;
    }

    if (response.status === 204 || response.status === 205) {
      return { data: null as T, status: 'success', timestamp: new Date().toISOString() };
    }

    let data: unknown = null;
    if (isJson) {
      try {
        data = await response.json();
      } catch {
        data = null;
      } // tolerante
    } else {
      data = await response.text();
      // Normalise an empty text body to `null` so it lines up with the JSON branch's own
      // empty-body coercion (json() throws on empty → catch → null). Without this, a Spring
      // `ResponseEntity.accepted().build()` (202 no body, no content-type) arrives here as `''`
      // and callers using `response.data == null` to detect maker-checker enqueues get a false
      // negative — the queued write surfaces as a success toast.
      // Scoped to the non-JSON branch so a legitimate JSON `""` body still round-trips as the
      // empty string. (javier-varela, PR #1147 fourth pass.)
      if (data === '') {
        data = null;
      }
    }

    return { data: data as T, status: 'success', timestamp: new Date().toISOString() };
  }

  /**
   * Handle API errors
   */
  private async handleError(error: unknown): Promise<ApiError> {
    let apiError: ApiError;

    if (isApiError(error)) {
      // type guard, basic case
      apiError = error as ApiError;
    } else {
      if (error instanceof Error) {
        // network or other error
        apiError = {
          message: error.message,
          code: 'NETWORK_ERROR',
          status: 0,
        };
      } else {
        // unknown error shape
        apiError = {
          message: 'Unknown error occurred',
          code: 'UNKNOWN_ERROR',
          status: 0,
        };
      }
    }

    // Apply error interceptors
    for (const interceptor of this.errorInterceptors) {
      apiError = await interceptor(apiError); // may modify the error, and keep chaining
    }

    return apiError;
  }
}

// Create and export default client instance
export const apiClient = new ApiClient();

// Export utility functions
export const createApiClient = (baseURL: string): ApiClient => {
  return new ApiClient(baseURL);
};

// Type guards
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'code' in error &&
    'status' in error
  );
};
